/**
 * מסמכים — the global documents centre the legacy left as an empty placeholder.
 *
 * The scan gate is the load-bearing part of this screen. A file is not downloadable until
 * it is clean, and the UI has to say so plainly: "בסריקה" with a disabled action, and a
 * blocked state for anything quarantined. Rendering a normal-looking download link that
 * 409s would teach users that the product is broken rather than careful.
 */
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDateTime } from "@onecpa/shared";
import {
  Badge,
  Button,
  Checkbox,
  type Column,
  DataTable,
  EmptyState,
  ErrorState,
  Input,
  KpiStrip,
  PageHeader,
  Select,
  StatusBadge,
} from "@onecpa/ui";
import { api, type FileRow } from "../api/client.js";
import { useSession } from "../app/session.js";

const CATEGORY_LABELS: Record<string, string> = {
  annual_report: "דוח שנתי",
  wealth_declaration: "הצהרת הון",
  authority_report: "דוח לרשות",
  monthly_material: "חומר חודשי",
  invoice: "חשבון",
  signed_document: "מסמך חתום",
  client_general: "כללי",
  other: "אחר",
};

export function Documents() {
  const session = useSession();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [scanStatus, setScanStatus] = useState("");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["documents", session.orgId, search, category, scanStatus],
    queryFn: () =>
      api.documents.list(session.orgId, {
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(category ? { category } : {}),
        ...(scanStatus ? { scanStatus } : {}),
      }),
  });

  const download = useMutation({
    mutationFn: (id: string) => api.documents.download(session.orgId, id),
    onSuccess: (result) => {
      // A short-lived signed URL, opened rather than fetched: the bytes never pass
      // through the app.
      window.open(result.url, "_blank", "noopener");
    },
    onError: (mutationError: Error) => session.toast(mutationError.message, "danger"),
  });

  const setVisibility = useMutation({
    mutationFn: (input: { id: string; clientVisible: boolean }) =>
      api.documents.update(session.orgId, input.id, { clientVisible: input.clientVisible }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["documents"] });
      session.toast("הרשאת הצפייה עודכנה");
    },
    onError: (mutationError: Error) => session.toast(mutationError.message, "danger"),
  });

  const rows = data?.files ?? [];

  const columns: Column<FileRow>[] = [
    {
      key: "name",
      header: "מסמך",
      sticky: true,
      width: "280px",
      cell: (row) => (
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-ink">
            {row.displayName}
          </span>
          <span className="block text-2xs text-ink-faint">
            {CATEGORY_LABELS[row.category] ?? row.category} ·{" "}
            <bdi className="tnum">{Math.max(1, Math.round(row.sizeBytes / 1024))} KB</bdi>
          </span>
        </span>
      ),
    },
    {
      key: "client",
      header: "לקוח",
      cell: (row) =>
        row.clientId ? (
          <Link
            to="/clients/$clientId"
            params={{ clientId: row.clientId }}
            className="link-underline text-xs text-ink"
          >
            {row.clientName}
          </Link>
        ) : (
          <span className="text-xs text-ink-faint">משרד</span>
        ),
    },
    {
      key: "uploaded",
      header: "הועלה",
      hideOnSmall: true,
      cell: (row) => (
        <span className="text-2xs text-ink-muted">
          <bdi className="tnum">{formatDateTime(row.createdAt)}</bdi>
          {row.uploadedByName ? ` · ${row.uploadedByName}` : ""}
        </span>
      ),
    },
    {
      key: "visibility",
      header: "גלוי ללקוח",
      align: "center",
      hideOnSmall: true,
      cell: (row) => (
        <Checkbox
          label=""
          aria-label={`הצגת "${row.displayName}" ללקוח`}
          checked={row.clientVisible}
          onChange={(event) =>
            setVisibility.mutate({ id: row.id, clientVisible: event.target.checked })
          }
        />
      ),
    },
    {
      key: "scan",
      header: "סריקה",
      align: "end",
      cell: (row) => (
        <span className="flex items-center justify-end gap-2">
          <StatusBadge vocabulary="scan" status={row.scanStatus} />
          {row.scanStatus === "clean" ? (
            <Button size="sm" onClick={() => download.mutate(row.id)}>
              הורדה
            </Button>
          ) : (
            <Button size="sm" disabled title={row.scanDetail ?? "הקובץ אינו זמין להורדה"}>
              הורדה
            </Button>
          )}
        </span>
      ),
    },
  ];

  if (error) {
    return <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />;
  }

  const counts = {
    total: rows.length,
    clean: rows.filter((row) => row.scanStatus === "clean").length,
    pending: rows.filter((row) => row.scanStatus === "pending").length,
    quarantined: rows.filter((row) => row.scanStatus === "quarantined").length,
    visible: rows.filter((row) => row.clientVisible).length,
  };

  return (
    <>
      <PageHeader title="מסמכים" subtitle={`${counts.total} מסמכים`} />

      <KpiStrip
        loading={isLoading}
        items={[
          { label: "סה״כ", value: counts.total },
          { label: "נסרקו", value: counts.clean, tone: "ledger" },
          { label: "בסריקה", value: counts.pending, tone: "pending" },
          {
            label: "בהסגר",
            value: counts.quarantined,
            tone: counts.quarantined > 0 ? "red" : "ink",
          },
          { label: "גלויים ללקוח", value: counts.visible },
        ]}
      />

      <div className="flex flex-wrap items-center gap-3 px-5 pt-4">
        <Input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="חיפוש מסמך"
          aria-label="חיפוש מסמך"
          className="max-w-xs rounded-pill"
        />
        <Select
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          aria-label="סינון לפי סוג"
          className="w-44"
        >
          <option value="">כל הסוגים</option>
          {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>

        <Select
          value={scanStatus}
          onChange={(event) => setScanStatus(event.target.value)}
          aria-label="סינון לפי מצב סריקה"
          className="w-40"
        >
          <option value="">כל מצבי הסריקה</option>
          <option value="clean">נסרקו</option>
          <option value="pending">בסריקה</option>
          <option value="quarantined">בהסגר</option>
          <option value="failed">סריקה נכשלה</option>
        </Select>

        {counts.quarantined > 0 && (
          <Badge tone="danger">{counts.quarantined} קבצים בהסגר — אינם זמינים להורדה</Badge>
        )}
      </div>

      <div className="p-5">
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          loading={isLoading}
          caption="מסמכי המשרד"
          footnote="קובץ אינו זמין להורדה עד שהסריקה מסתיימת · קובץ בהסגר נחסם לצמיתות"
          empty={<EmptyState title="אין מסמכים" icon="file-text" />}
        />
      </div>
    </>
  );
}
